import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Package, Search, SlidersHorizontal, ShoppingCart, Loader2, CheckCircle2,
  Store, TrendingUp, ArrowUpDown, X, Shirt, Smartphone, Home as HomeIcon,
  Sparkles, Dumbbell, Baby, Car, BookOpen, Palette
} from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Vitrine sem foto real de produto (nenhum fornecedor ainda cadastrou
// imagens) - em vez de inventar foto, usa um estilo visual consistente
// por categoria, honesto e ainda assim profissional.
const CATEGORIA_ESTILO = {
  "Eletrônicos": { icon: Smartphone, cor: "from-blue-500 to-cyan-500" },
  "Moda": { icon: Shirt, cor: "from-pink-500 to-rose-500" },
  "Casa": { icon: HomeIcon, cor: "from-amber-500 to-orange-500" },
  "Beleza": { icon: Sparkles, cor: "from-purple-500 to-fuchsia-500" },
  "Esportes": { icon: Dumbbell, cor: "from-green-500 to-emerald-500" },
  "Infantil": { icon: Baby, cor: "from-yellow-400 to-amber-400" },
  "Automotivo": { icon: Car, cor: "from-slate-500 to-gray-600" },
  "Livros": { icon: BookOpen, cor: "from-indigo-500 to-blue-600" },
};
const estiloPadrao = { icon: Package, cor: "from-orange-500 to-red-500" };
const estiloCategoria = (cat) => CATEGORIA_ESTILO[cat] || estiloPadrao;

const ORDENACOES = [
  { id: "relevancia", label: "Mais relevantes" },
  { id: "mais_vendidos", label: "Mais vendidos" },
  { id: "menor_preco", label: "Menor preço" },
  { id: "maior_preco", label: "Maior preço" },
];

const Catalog = () => {
  const USUARIO_ID = authService.getUser()?.id || "";

  const [searchTerm, setSearchTerm] = useState("");
  const [buscaAtiva, setBuscaAtiva] = useState("");
  const [categoriaAtiva, setCategoriaAtiva] = useState("");
  const [ordenar, setOrdenar] = useState("relevancia");
  const [precoMin, setPrecoMin] = useState("");
  const [precoMax, setPrecoMax] = useState("");
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [vinculando, setVinculando] = useState(null);
  const [vinculados, setVinculados] = useState(new Set());

  const carregarProdutos = useCallback(async () => {
    setCarregando(true);
    try {
      const params = new URLSearchParams({ limit: "60", ordenar });
      if (buscaAtiva) params.set("busca", buscaAtiva);
      if (categoriaAtiva) params.set("categoria", categoriaAtiva);
      if (precoMin) params.set("preco_min", precoMin);
      if (precoMax) params.set("preco_max", precoMax);

      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/catalogo-geral?${params}`);
      if (!resp.ok) throw new Error(`Falha ao carregar catálogo (${resp.status})`);
      const data = await resp.json();
      setProdutos(data.produtos || []);
      setTotal(data.total || 0);
      setCategorias(data.categorias_disponiveis || []);
    } catch (error) {
      console.error("Erro ao carregar catálogo:", error);
      toast.error("Não foi possível carregar o catálogo.");
    } finally {
      setCarregando(false);
    }
  }, [buscaAtiva, categoriaAtiva, ordenar, precoMin, precoMax]);

  useEffect(() => {
    carregarProdutos();
  }, [carregarProdutos]);

  const vincular = async (produto) => {
    setVinculando(produto.id);
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/afiliado/${USUARIO_ID}/vincular-produto`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ produto_id: produto.id }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao vincular produto");
      toast.success(`"${produto.nome}" vinculado! Já está no seu catálogo.`);
      setVinculados((prev) => new Set(prev).add(produto.id));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setVinculando(null);
    }
  };

  const limparFiltros = () => {
    setSearchTerm(""); setBuscaAtiva(""); setCategoriaAtiva("");
    setOrdenar("relevancia"); setPrecoMin(""); setPrecoMax("");
  };

  const temFiltrosAtivos = categoriaAtiva || precoMin || precoMax || buscaAtiva;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Cabeçalho estilo vitrine */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Store className="w-7 h-7 text-orange-400" />
          <h1 className="text-2xl font-bold text-white">Vitrine do Catálogo</h1>
        </div>
        <p className="text-sm text-gray-400">Produtos de todos os fornecedores da plataforma - escolha o que vender</p>
      </div>

      {/* Barra de busca principal */}
      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Buscar produto pelo nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setBuscaAtiva(searchTerm)}
            className="pl-9 h-11"
          />
        </div>
        <Button onClick={() => setBuscaAtiva(searchTerm)} className="bg-orange-600 hover:bg-orange-500 h-11 px-6">
          <Search className="w-4 h-4 mr-2" />
          Buscar
        </Button>
        <Button variant="outline" onClick={() => setFiltrosAbertos(!filtrosAbertos)} className="h-11">
          <SlidersHorizontal className="w-4 h-4 mr-2" />
          Filtros
        </Button>
      </div>

      {/* Categorias em chips - navegação estilo marketplace */}
      {categorias.length > 0 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <button
            onClick={() => setCategoriaAtiva("")}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !categoriaAtiva ? "bg-orange-600 text-white" : "bg-gray-800 text-gray-300 hover:bg-gray-700"
            }`}
          >
            Todas as categorias
          </button>
          {categorias.map((cat) => {
            const { icon: Icone } = estiloCategoria(cat);
            return (
              <button
                key={cat}
                onClick={() => setCategoriaAtiva(cat)}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  categoriaAtiva === cat ? "bg-orange-600 text-white" : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                }`}
              >
                <Icone className="w-3.5 h-3.5" />
                {cat}
              </button>
            );
          })}
        </div>
      )}

      {/* Painel de filtros avançados */}
      {filtrosAbertos && (
        <Card className="p-4 mb-4 bg-gray-800/50 border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Preço mínimo</label>
              <Input type="number" placeholder="R$ 0" value={precoMin} onChange={(e) => setPrecoMin(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Preço máximo</label>
              <Input type="number" placeholder="R$ 9999" value={precoMax} onChange={(e) => setPrecoMax(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-400 mb-1 block">Ordenar por</label>
              <div className="flex gap-2 flex-wrap">
                {ORDENACOES.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => setOrdenar(o.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium ${
                      ordenar === o.id ? "bg-orange-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    {o.id === "mais_vendidos" && <TrendingUp className="w-3 h-3" />}
                    {(o.id === "menor_preco" || o.id === "maior_preco") && <ArrowUpDown className="w-3 h-3" />}
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Barra de resultado + limpar filtros */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-400">
          {carregando ? "Carregando..." : `${total} produto${total !== 1 ? "s" : ""} encontrado${total !== 1 ? "s" : ""}`}
        </p>
        {temFiltrosAtivos && (
          <button onClick={limparFiltros} className="flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300">
            <X className="w-3 h-3" /> Limpar filtros
          </button>
        )}
      </div>

      {/* Grade de produtos - vitrine */}
      {carregando ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando vitrine...
        </div>
      ) : produtos.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-semibold mb-2 text-white">Nenhum produto encontrado</h3>
          <p className="text-gray-400">Tente ajustar os filtros, ou volte mais tarde quando novos fornecedores cadastrarem produtos.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {produtos.map((produto) => {
            const jaVinculado = vinculados.has(produto.id);
            const { icon: Icone, cor } = estiloCategoria(produto.categoria);
            return (
              <Card
                key={produto.id}
                className="group bg-gray-800/50 border-gray-700 overflow-hidden flex flex-col transition-all duration-200 hover:border-orange-500 hover:-translate-y-1 hover:scale-[1.02] hover:shadow-2xl hover:shadow-orange-500/20 cursor-default"
              >
                {/* "Foto" da vitrine - estilo visual por categoria, honesto (sem foto falsa) */}
                <div className={`h-32 bg-gradient-to-br ${cor} flex items-center justify-center relative overflow-hidden`}>
                  <Icone className="w-12 h-12 text-white/90 transition-transform duration-300 group-hover:scale-125" />
                  {produto.estoque < 10 && (
                    <span className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                      Últimas {produto.estoque}
                    </span>
                  )}
                </div>

                <div className="p-3 flex flex-col gap-2 flex-1">
                  <p className="text-[11px] text-gray-500 flex items-center gap-1">
                    <Store className="w-3 h-3" /> {produto.fornecedor_nome}
                  </p>
                  <h3 className="text-white font-semibold text-sm leading-snug line-clamp-2 min-h-[2.5em]">{produto.nome}</h3>

                  <div>
                    <p className="text-lg font-bold text-orange-400">R$ {(produto.preco || 0).toFixed(2)}</p>
                    <p className="text-[11px] text-green-400">Comissão de {produto.taxa_comissao || 10}%</p>
                  </div>

                  <div className="flex items-center justify-between text-[11px] text-gray-400 pt-1 border-t border-gray-700">
                    <span className="flex items-center gap-1"><ShoppingCart className="w-3 h-3" /> {produto.vendas || 0} vendas</span>
                    <span>{produto.estoque} un</span>
                  </div>

                  <Button
                    size="sm"
                    className="w-full bg-orange-600 hover:bg-orange-500 text-xs mt-auto"
                    onClick={() => vincular(produto)}
                    disabled={jaVinculado || vinculando === produto.id}
                  >
                    {vinculando === produto.id ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : jaVinculado ? (
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                    ) : null}
                    {jaVinculado ? "Vinculado" : "Vincular"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Catalog;
